import type { CustomCriterionScore } from "./schema";

/** Human-readable value for one custom criterion score: "Sim"/"Não" for
 * binary, "x/max" (or "x (min–max)" when the scale doesn't start at 0) for
 * numeric. `emoji` style swaps the binary labels for ✅/❌ (Slack summaries). */
export function formatCriterionScore(
  score: CustomCriterionScore,
  style: "text" | "emoji" = "text",
): string {
  if (score.scaleType === "binary") {
    if (style === "emoji") return score.score === 1 ? "✅" : "❌";
    return score.score === 1 ? "Sim" : "Não";
  }
  const min = score.scaleMin ?? 0;
  if (min === 0) return `${score.score}/${score.scaleMax}`;
  return `${score.score} (${min}–${score.scaleMax})`;
}

/** Position of one score within its scale, normalized to 0..1
 * ((score − min) / (max − min); binary min/max = 0/1). */
export function criterionScoreFraction(score: CustomCriterionScore): number {
  const min = score.scaleType === "binary" ? 0 : (score.scaleMin ?? 0);
  const max = score.scaleType === "binary" ? 1 : (score.scaleMax ?? 0);
  if (max <= min) return score.score;
  return (score.score - min) / (max - min);
}

/** Average of a custom-form submission, normalized to 0..1 — each score is
 * normalized within its own scale. Null for empty input. */
export function customCriteriaAverage(scores: CustomCriterionScore[]): number | null {
  if (scores.length === 0) return null;
  const fractions = scores.map(criterionScoreFraction);
  return fractions.reduce((sum, f) => sum + f, 0) / fractions.length;
}

/** One-line summary of all criteria of a submission, e.g.
 * "Comunicação ✅ | Qualidade 3/5". Used by Slack/cron surfaces. */
export function summarizeCustomCriteria(
  scores: CustomCriterionScore[],
  style: "text" | "emoji" = "emoji",
): string {
  return scores.map((s) => `${s.label} ${formatCriterionScore(s, style)}`).join(" | ");
}
